import {
  BarChart3,
  CalendarDays,
  Clock3,
  CreditCard,
  Download,
  Inbox,
  LayoutGrid,
  Settings,
  ShoppingBag,
  UtensilsCrossed,
  Wallet,
} from 'lucide-react';

export const POS_HOME_ICON_COMPONENTS = {
  BarChart3,
  CalendarDays,
  Clock3,
  CreditCard,
  Download,
  Inbox,
  LayoutGrid,
  Settings,
  ShoppingBag,
  UtensilsCrossed,
  Wallet,
};

export const POS_HOME_ICON_OPTIONS = [
  { value: 'ShoppingBag', label: 'Kasse' },
  { value: 'UtensilsCrossed', label: 'Tische' },
  { value: 'Inbox', label: 'Offene Bestellungen' },
  { value: 'CreditCard', label: 'Checkout / Bezahlen' },
  { value: 'Wallet', label: 'Umsatz / Kasse' },
  { value: 'BarChart3', label: 'Berichte' },
  { value: 'Download', label: 'Online Bestellungen' },
  { value: 'CalendarDays', label: 'Reservierungen' },
  { value: 'Clock3', label: 'Zeiterfassung' },
  { value: 'Settings', label: 'Einstellungen' },
  { value: 'LayoutGrid', label: 'Allgemein' },
];

export const POS_HOME_ROUTE_OPTIONS = [
  { value: '/pos/terminal', label: 'Kasse / Bonieren' },
  { value: '/pos/tables', label: 'Tische' },
  { value: '/pos/orders', label: 'Offene Bestellungen' },
  { value: '/pos/invoices', label: 'Rechnungen / Checkout' },
  { value: '/pos/online-orders', label: 'Online Bestellungen' },
  { value: '/pos/berichtswesen', label: 'Berichte' },
  { value: '/pos/reservations', label: 'Reservierungen' },
  { value: '/pos/time-tracking', label: 'Zeiterfassung' },
  { value: '/pos/settings', label: 'Einstellungen' },
];

const DEFAULT_POS_HOME_SHORTCUTS = [
  {
    id: 'terminal',
    path: '/pos/terminal',
    title: 'Kasse',
    description: 'Bonieren und verkaufen',
    icon: 'ShoppingBag',
    enabled: true,
    sort_order: 0,
    tileColor: '#ffffff',
    accentColor: '#2f2f31',
    textColor: '#262729',
  },
  {
    id: 'tables',
    path: '/pos/tables',
    title: 'Tische',
    description: 'Raumplan und Tische',
    icon: 'UtensilsCrossed',
    enabled: true,
    sort_order: 1,
    tileColor: '#ffffff',
    accentColor: '#2f2f31',
    textColor: '#262729',
  },
  {
    id: 'invoices',
    path: '/pos/invoices',
    title: 'Checkout',
    description: 'Zahlungen und Belege',
    icon: 'CreditCard',
    enabled: true,
    sort_order: 2,
    tileColor: '#ffffff',
    accentColor: '#2f2f31',
    textColor: '#262729',
  },
  {
    id: 'reports',
    path: '/pos/berichtswesen',
    title: 'Umsatz',
    description: 'Tageszahlen prüfen',
    icon: 'Wallet',
    enabled: true,
    sort_order: 3,
    tileColor: '#ffffff',
    accentColor: '#2f2f31',
    textColor: '#262729',
  },
  {
    id: 'orders',
    path: '/pos/orders',
    title: 'Bestellungen',
    description: 'Offene Vorgänge',
    icon: 'Inbox',
    enabled: true,
    sort_order: 4,
    tileColor: '#ffffff',
    accentColor: '#2f2f31',
    textColor: '#262729',
  },
  {
    id: 'online-orders',
    path: '/pos/online-orders',
    title: 'Online',
    description: 'Webshop und Partner',
    icon: 'Download',
    enabled: false,
    sort_order: 5,
    tileColor: '#ffffff',
    accentColor: '#2f2f31',
    textColor: '#262729',
  },
  {
    id: 'reservations',
    path: '/pos/reservations',
    title: 'Reservierungen',
    description: 'Buchungen verwalten',
    icon: 'CalendarDays',
    enabled: false,
    sort_order: 6,
    tileColor: '#ffffff',
    accentColor: '#2f2f31',
    textColor: '#262729',
  },
  {
    id: 'time-tracking',
    path: '/pos/time-tracking',
    title: 'Zeiterfassung',
    description: 'Schichten und Sessions',
    icon: 'Clock3',
    enabled: false,
    sort_order: 7,
    tileColor: '#ffffff',
    accentColor: '#2f2f31',
    textColor: '#262729',
  },
  {
    id: 'settings',
    path: '/pos/settings',
    title: 'Einstellungen',
    description: 'Geraet und Kasse',
    icon: 'Settings',
    enabled: false,
    sort_order: 8,
    tileColor: '#ffffff',
    accentColor: '#2f2f31',
    textColor: '#262729',
  },
];

function validIconName(value) {
  return POS_HOME_ICON_COMPONENTS[String(value || '').trim()] ? String(value || '').trim() : 'LayoutGrid';
}

function validRoute(value) {
  return POS_HOME_ROUTE_OPTIONS.some((option) => option.value === value)
    ? value
    : '/pos/home';
}

export function buildDefaultPosHomeShortcuts() {
  return DEFAULT_POS_HOME_SHORTCUTS.map((shortcut) => ({ ...shortcut }));
}

export function normalizePosHomeShortcuts(shortcuts) {
  const defaults = buildDefaultPosHomeShortcuts();
  const configured = Array.isArray(shortcuts) ? shortcuts : [];
  const configuredById = new Map(configured.map((entry) => [entry?.id, entry]));

  return defaults
    .map((entry, index) => {
      const override = configuredById.get(entry.id) || {};
      return {
        ...entry,
        ...override,
        id: entry.id,
        path: validRoute(override.path || entry.path),
        icon: validIconName(override.icon || entry.icon),
        title: String(override.title || entry.title),
        description: String(override.description || entry.description || ''),
        enabled: override.enabled !== false,
        sort_order: Number.isFinite(Number(override.sort_order)) ? Number(override.sort_order) : index,
        tileColor: String(override.tileColor || entry.tileColor),
        accentColor: String(override.accentColor || entry.accentColor),
        textColor: String(override.textColor || entry.textColor),
      };
    })
    .sort((left, right) => left.sort_order - right.sort_order);
}

export function resolvePosHomeIcon(iconKey) {
  return POS_HOME_ICON_COMPONENTS[validIconName(iconKey)] || LayoutGrid;
}
